<template>
    <CompassDialog
        ref="ConsentDialog"
        :visible.sync="showModal"
        size="small"
        :title="$t.__('sellsyadmin.ConnectionConsentDialog.access_request')"
        :closeOnClickOverlay="false"
        class="consent-dialog"
        @closed="closeModal"
    >
        <template #default>
            <CompassRadioGroup
                v-show="demandType === 'service'"
                v-model="authorization"
                :label="$t.__('sellsyadmin.ConnectionConsentDialog.request_type')"
                direction="horizontal"
                size="medium"
                class="radiogroup-consent"
            >
                <CompassRadio
                    modelValue="service"
                    :label="$t.__('sellsyadmin.ConnectionConsentDialog.services')"
                />
                <CompassRadio
                    modelValue="support"
                    :label="$t.__('sellsyadmin.ConnectionConsentDialog.support')"
                />
            </CompassRadioGroup>
            <CompassFormItem label="Collaborateur" class="select-collaborator">
                <CompassSelect
                    v-model="selectedStaff"
                    size="medium"
                    :placeholder="$t.__('common.actions.select_staff')"
                    filterable
                    menuClass="staff-selector"
                    :disabled="disabledSelect"
                >
                    <CompassDropdownSelectOption
                        v-if="!filteredStaff.inactive.length"
                        value="no staff"
                        :label="$t.__('sellsyadmin.ConnectionConsentDialog.no_staff_available')"
                    />
                    <CompassOptionGroup
                        v-if="filteredStaff.pending.length > 0"
                        :label="$t.__('common.labels.access_awaiting_authorization')"
                    >
                        <CompassDropdownSelectOption
                            v-for="option in filteredStaff.pending"
                            :key="option.staffid"
                            :value="option.staffid"
                            :label="option.fullName"
                            :labelCaption="labelCaptionStaffID(option.staffid)"
                            :avatar="option.avatar"
                            type="avatar"
                            :disabled="true"
                        />
                    </CompassOptionGroup>
                    <CompassOptionGroup v-if="filteredStaff.inactive.length > 0">
                        <CompassDropdownSelectOption
                            v-for="option in filteredStaff.inactive"
                            :key="option.staffid"
                            :value="option.staffid"
                            :label="option.fullName"
                            :labelCaption="labelCaptionStaffID(option.staffid)"
                            :avatar="option.avatar"
                            type="avatar"
                        />
                    </CompassOptionGroup>
                </CompassSelect>
            </CompassFormItem>
            <CompassRadioGroup
                v-show="demandType === 'support'"
                v-model="source"
                :label="$t.__('sellsyadmin.ConnectionConsentDialog.request_origin')"
                direction="horizontal"
                size="medium"
                class="radiogroup-consent"
            >
                <CompassRadio
                    modelValue="intercom"
                    :label="$t.__('sellsyadmin.ConnectionConsentDialog.intercom')"
                />
                <CompassRadio
                    modelValue="sellsy"
                    :label="$t.__('sellsyadmin.ConnectionConsentDialog.admin')"
                />
            </CompassRadioGroup>
            <CompassFormItem
                :label="customMessageTranslate.label"
                :info="getInfoCustomMessage"
                :error="getErrorCustomMessage"
            >
                <CompassInput
                    v-model="customMessage"
                    type="textarea"
                    :placeholder="customMessageTranslate.placeholder"
                    :error="exceedsLimit"
                />
            </CompassFormItem>
        </template>
        <template #footer>
            <div class="dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="closeModal"
                />
                <CompassButton
                    :label="$t.__('common.actions.send')"
                    :disabled="disableFormElements"
                    :loading="loading"
                    @click="handleRequest"
                />
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import { requestConsent } from 'api/impersonate/impersonateService';
import AvatarMixins from 'mixins/AvatarMixins';

export default {
    mixins: [AvatarMixins],
    props: {
        demandType: {
            type: String,
            default: 'suport',
            validator(value) {
                return value === 'support' || value === 'service';
            },
        },
        staff: {
            type: Array,
            default: () => [],
        },
        thirdId: {
            type: Number,
            default: null,
        },
        corpId: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            showModal: true,
            authorization: this.demandType,
            source: this.demandType === 'support' ? 'intercom' : 'sellsy',
            selectedStaff: '',
            loading: false,
            customMessage: '',
            maxCharactersCustomMessage: 255,
            customMessageTranslate: {
                label: this.$l10n.__('Joindre un message personnalisé à l’email système'),
                placeholder: this.$l10n.__('Saisissez ici votre message'),
            },
            characterTranslate: this.$l10n.__('caractères'),
            metadata: {},
        };
    },
    computed: {
        filteredStaff() {
            const inactive = this.staff
                .filter(
                    (item) =>
                        item.authorizations[this.authorization] === 'inactive' &&
                        !Object.values(item.authorizations).includes('advitam') &&
                        item.staffType !== 'sellsy',
                )
                .map((item) => this.addAvatar(item));
            const pending = this.staff
                .filter(
                    (item) =>
                        item.authorizations[this.authorization] === 'pending' &&
                        !Object.values(item.authorizations).includes('advitam') &&
                        item.staffType !== 'sellsy',
                )
                .map((item) => this.addAvatar(item));

            return {
                inactive,
                pending,
            };
        },
        disabledSelect() {
            return (
                this.filteredStaff.inactive.length === 0 && this.filteredStaff.pending.length === 0
            );
        },
        disableFormElements() {
            return this.filteredStaff.inactive.length === 0 || this.exceedsLimit;
        },
        exceedsLimit() {
            return this.customMessage.length > this.maxCharactersCustomMessage;
        },
        getInfoCustomMessage() {
            return `${this.customMessage.length} / ${this.maxCharactersCustomMessage} ${this.characterTranslate}`;
        },
        getErrorCustomMessage() {
            if (this.exceedsLimit) {
                return this.getInfoCustomMessage;
            }
            return '';
        },
    },
    watch: {
        visible(newValue) {
            this.showModal = newValue;
        },
        filteredStaff(newValue) {
            this.$nextTick(() => {
                this.selectedStaff = newValue.inactive[0]?.staffid ?? 'no staff';
            });
        },
    },
    created() {
        this.addMetadataAndCheckSource();
    },
    mounted() {
        this.selectedStaff = this.filteredStaff.inactive[0]?.staffid ?? 'no staff';
    },
    methods: {
        labelCaptionStaffID(id) {
            return `STAFFID ${id}`;
        },
        addMetadataAndCheckSource() {
            const urlParams = new URLSearchParams(window.location.search);
            const tid = urlParams.get('tid');

            this.source = tid ? 'sellsy' : this.source;

            this.metadata = {
                linked_object: {
                    type: tid ? 'ticket' : 'company',
                    id: tid ? parseInt(tid) : this.thirdId,
                },
            };
        },
        addAvatar(staff) {
            return {
                ...staff,
                avatar: {
                    initials: staff.forename.charAt(0),
                    state: 'default',
                    colorType: this.avatarColor(staff.fullName),
                },
            };
        },
        handleRequest() {
            this.loading = true;

            requestConsent({
                type: this.authorization,
                source: this.source,
                targeted_staff_id: parseInt(this.selectedStaff),
                targeted_corp_id: this.corpId,
                message: this.customMessage,
                source_metadata: this.metadata,
            })
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        text: this.$l10n.__('La demande a bien été envoyée'),
                        close: true,
                    });
                    this.$emit('success');
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$l10n.__('Une erreur est survenue, merci de réessayer'),
                        close: true,
                    });
                    this.$emit('close');
                })
                .finally(() => {
                    this.loading = false;
                    this.showModal = false;
                });
        },
        closeModal() {
            this.showModal = false;
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss" scoped>
.consent-dialog {
    .compass-dialog__modal__content {
        p {
            margin: 0;
        }
    }

    .compass-dialog__modal__footer {
        .dialog__footer {
            display: flex;
            flex-direction: row;
            gap: var(--spacing-3);
            justify-content: flex-end;

            padding: var(--spacing-4) var(--spacing-5) var(--spacing-5);
        }
    }

    .select-collaborator,
    .radiogroup-consent {
        margin: 0 0 var(--spacing-3);
    }
}
</style>
<style lang="scss">
.consent-dialog {
    .compass-form-item__caption {
        text-align: end !important;
    }
}
#compass-dropdown-container {
    .staff-selector {
        z-index: 1001;
    }
}
</style>
